export enum MessageType {
  BUFFERED = 'BUFFERED',
  STREAM = 'STREAM',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

export type Action = {
  id: string;
  name: string;
  description: string;
  context?: {
    actions?: Action[];
    multi_select?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
};

export type Agent = {
  agent_type: string;
  actions?: Action[];
};

export enum AgentType {
  copilot = 'copilot',
}

export type Message = {
  id: string;
  conversation_id: string;
  content: string;
  is_user: boolean;
  context?: {
    type?: keyof typeof MessageType;
    content_type?: 'thinking' | 'content';
    steps?: string[];
    thinking_count?: number;
    actions?: Action[];
    invalidate_queries?: string[][];
    conversation_id?: string;
    agent_type?: string;
    agent_name?: string;
  } | null;
  active_llm_service_context?: string | null;
  created_at: string;
  modified_at: string;
};

export type Conversation = {
  id: string;
  title: string;
  organization: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  messages?: Message[];
};

export enum AnnotationRating {
  positive = 'positive',
  negative = 'negative',
}

export enum AnnotationCategory {
  hallucination = 'hallucination',
  incorrect = 'incorrect',
  incomplete = 'incomplete',
  misleading = 'misleading',
  wrong_format = 'wrong_format',
  other = 'other',
}

export type Annotation = {
  id: string;
  message: Message;
  annotated_by_id: string;
  annotated_by_email: string;
  rating: AnnotationRating | null;
  comment: string | null;
  category: AnnotationCategory | null;
  is_resolved: boolean;
  created_at: string;
  modified_at: string;
};

export type TokenUsage = {
  id: string;
  organization_id: string;
  valid_from: string;
  valid_to: string;
  tokens_used: number;
  tokens_allowed: number;
  usage_percentage?: number | null;
  created_at: string;
  modified_at: string;
};

/**
 * GET /api/v1/organization/{organization_id}/agents
 */
export const GET_ORGANIZATION_AGENTS_PATH = 'organization/{organization_id}/agents';
export type GetOrganizationAgentsRequestPathVariables = { organization_id: string };
export type GetOrganizationAgentsRequestQueryParams = {
  agent_type?: string;
};
export type GetOrganizationAgentsSuccessDto = Agent[];
export type GetOrganizationAgentsErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/conversations
 */
export const GET_ORGANIZATION_CONVERSATIONS_PATH = 'organization/{organization_id}/conversations';
export type GetOrganizationConversationsRequestPathVariables = { organization_id: string };
export type GetOrganizationConversationsRequestQueryParams =
  | {
      conversation_id?: string;
    }
  | undefined;
export type GetOrganizationConversationsSuccessDto = Conversation[];
export type GetOrganizationConversationsErrorDto = Error;

/**
 * POST /api/v1/organization/{organization_id}/conversations
 */
export const POST_ORGANIZATION_CONVERSATIONS_PATH = 'organization/{organization_id}/conversations';
export type PostOrganizationConversationsRequestPathVariables = { organization_id: string };
export type PostOrganizationConversationsRequestQueryParams =
  | {
      agent_type?: string;
    }
  | undefined;
export type PostOrganizationConversationsRequestBodyData = Partial<Message>;
export type PostOrganizationConversationsSuccessDto = unknown;
export type PostOrganizationConversationsErrorDto = Error;

const ORGANIZATION_ANNOTATIONS_PATH = 'organization/{organization_id}/annotations';
/**
 * GET /api/v1/organization/{organization_id}/annotations
 */
export const GET_ORGANIZATION_ANNOTATIONS_PATH = ORGANIZATION_ANNOTATIONS_PATH;
export type GetOrganizationAnnotationsPathVariables = { organization_id: string };
export type GetOrganizationAnnotationsQueryParams =
  | {
      conversation_id?: string;
      message_id?: string;
      annotated_by_id?: string;
    }
  | undefined;
export type GetOrganizationAnnotationsSuccessDto = Annotation[];
export type GetOrganizationAnnotationsErrorDto = Error;

/**
 * POST /api/v1/organization/{organization_id}/annotations
 */
export const POST_ORGANIZATION_ANNOTATIONS_PATH = ORGANIZATION_ANNOTATIONS_PATH;
export type PostOrganizationAnnotationsPathVariables = { organization_id: string };
export type PostOrganizationAnnotationsQueryParams = {
  conversation_id: string;
  message_id: string;
};
export type PostOrganizationAnnotationsBody = {
  rating: AnnotationRating;
  comment?: string | null;
  category?: AnnotationCategory | null;
};
export type PostOrganizationAnnotationsSuccessDto = Annotation;
export type PostOrganizationAnnotationsErrorDto = Error;

/**
 * PATCH /api/v1/organization/{organization_id}/annotations
 */
export const PATCH_ORGANIZATION_ANNOTATIONS_PATH = ORGANIZATION_ANNOTATIONS_PATH;
export type PatchOrganizationAnnotationsPathVariables = { organization_id: string };
export type PatchOrganizationAnnotationsQueryParams = { annotation_id: string };
export type PatchOrganizationAnnotationsBody = Partial<Annotation>;
export type PatchOrganizationAnnotationsSuccessDto = Annotation;
export type PatchOrganizationAnnotationsErrorDto = Error;
/**
 * DELETE /api/v1/organization/{organization_id}/annotations
 */
export const DELETE_ORGANIZATION_ANNOTATIONS_PATH = ORGANIZATION_ANNOTATIONS_PATH;
export type DeleteOrganizationAnnotationsPathVariables = { organization_id: string };
export type DeleteOrganizationAnnotationsQueryParams = { annotation_id: string };
export type DeleteOrganizationAnnotationsBody = Partial<Annotation>;
export type DeleteOrganizationAnnotationsSuccessDto = Annotation;
export type DeleteOrganizationAnnotationsErrorDto = Error;

const ORGANIZATION_TOKEN_USAGE_PATH = 'organization/{organization_id}/token-usage';
/**
 * GET /api/v1/organization/{organization_id}/token-usage
 */
export const GET_ORGANIZATION_TOKEN_USAGE_PATH = ORGANIZATION_TOKEN_USAGE_PATH;
export type GetOrganizationTokenUsagePathVariables = { organization_id: string };
export type GetOrganizationTokenUsageQueryParams =
  | {
      id?: string;
      current_date?: string;
    }
  | undefined;
export type GetOrganizationTokenUsageSuccessDto = TokenUsage[];
export type GetOrganizationTokenUsageErrorDto = Error;

/**
 * GET /api/v1/conversations
 */
export const GET_GLOBAL_CONVERSATIONS_PATH = 'conversations';
export type GetGlobalConversationsQueryParams =
  | {
      created_by?: string;
      conversation_id?: string;
    }
  | undefined;
export type GetGlobalConversationsSuccessDto = Conversation[];
export type GetGlobalConversationsErrorDto = Error;

/**
 * GET /api/v1/annotations
 */
export const GET_GLOBAL_ANNOTATIONS_PATH = 'annotations';
export type GetGlobalAnnotationsQueryParams =
  | {
      conversation_id?: string;
      message_id?: string;
      annotated_by_id?: string;
    }
  | undefined;
export type GetGlobalAnnotationsSuccessDto = Annotation[];
export type GetGlobalAnnotationsErrorDto = Error;
